/**
 * The column beside a room's tabs: its next live (or the one that ran, or who is watching now), where its people
 * came from, and who registered last.
 */
import { type ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import type { HostRoomRow } from "../../../shared/talk";
import type { RoomPerson, WebinarDetail, WebinarLive } from "../../../shared/types";
import { daysUntil, labelParts } from "../../../shared/live-time";
import { studioPath } from "../../board/LiveStrip";
import { Button } from "../../components/ui/Button";
import { Face, FaceStack, Ring } from "../../components/ui/Face";
import { api } from "../../lib/api";
import { lastRun, longDay } from "./liveWords";
import { RoomLanguage } from "./RoomLanguage";
import { firstOf, namesLine } from "./roomWords";
import { RoomTalkCard } from "./RoomTalk";

const n = (v: number) => v.toLocaleString("en-US");

/** "Next live · tomorrow", "Next live · Tuesday", "Live now". */
export function nextEyebrow(live: Pick<WebinarLive, "state" | "startsAt" | "timezone">, now: number): string {
  if (live.state === "live") return "Live now";
  const days = daysUntil(live.startsAt, live.timezone, now);
  if (days === 0) return "Next live · today";
  if (days === 1) return "Next live · tomorrow";
  return "Next live";
}

/** When someone registered, as the rail says it: "4 min ago", "Today", "Tue 27 Oct". */
export function whenRegistered(at: number, nowSec: number, tz: string): string {
  const ago = nowSec - at;
  if (ago < 3600) return `${Math.max(1, Math.round(ago / 60))} min ago`;
  if (ago < 86_400) return "Today";
  const p = labelParts(new Date(at * 1000).toISOString(), tz, "short");
  return p ? `${p.weekday} ${p.day} ${p.month}` : "";
}

/** The faces of the first few who came to a session, for the Was live card. */
export function useCameFaces(webinarId: string, sessionId: string, cameLive: number): RoomPerson[] {
  const [people, setPeople] = useState<RoomPerson[]>([]);
  useEffect(() => {
    if (cameLive === 0) {
      setPeople([]);
      return;
    }
    let alive = true;
    api.webinars
      .audienceGroup(webinarId, "came", sessionId)
      .then((r) => alive && setPeople(r.people.slice(0, 5)))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [webinarId, sessionId, cameLive]);
  return people;
}

/** What the room's top card says once its live has run: the day, whether the replay is ready, who came. */
function WasLive({ detail, live, watchingRow, onWatchReplay }: { detail: WebinarDetail; live: WebinarLive; watchingRow: ReactNode; onWatchReplay: () => void }) {
  const ran = live.series ? lastRun(live.series.sessions) : null;
  const at = ran ?? live;
  const came = ran ? ran.cameLive : live.cameLive;
  const faces = useCameFaces(detail.id, ran ? ran.id : live.sessionId, came);
  return (
    <section className="card">
      <div className="card-b">
        <div className="eyebrow">Was live</div>
        <div className="h2 mt4" style={{ fontSize: 18 }}>
          {longDay(at.startsAt, at.timezone)}
        </div>
        <div className="muted small">
          {labelParts(at.startsAt, at.timezone, "long")?.time}
          {live.replay.playable ? " · the replay is ready" : ""}
        </div>
        <div className="aud mt12">
          {faces.length ? <FaceStack people={faces.map((p) => ({ name: p.name, photo: p.avatar_url }))} size={28} more={Math.max(0, came - faces.length)} /> : null}
          <div className="t">
            <b>{n(came)} came</b>
            {ran ? "" : `, ${n(live.stayedToEnd)} stayed to the end`}
          </div>
        </div>
        {live.replay.playable ? (
          <div className="row g8 mt16">
            <Button size="s" icon="play" onClick={onWatchReplay}>
              Watch the replay
            </Button>
          </div>
        ) : null}
        {watchingRow ? (
          <>
            <div className="divider" />
            {watchingRow}
          </>
        ) : null}
      </div>
    </section>
  );
}

function TopCard({
  detail,
  onChangeTime,
  changeTime,
  onWatchReplay,
}: {
  detail: WebinarDetail;
  onChangeTime: () => void;
  /** The change-time popover, drawn under its button. */
  changeTime: ReactNode;
  onWatchReplay: () => void;
}) {
  const navigate = useNavigate();
  const live = detail.live;
  const now = Date.now();
  const watcher = detail.watching[0];
  const watchingRow = watcher ? (
    <button type="button" className="who whobtn" onClick={() => navigate(`/people/${watcher.registrantId}`)}>
      <Ring name={watcher.name} photo={watcher.avatar_url} size={34} percent={watcher.pct} live />
      <div>
        <div className="nm">Watching now: <bdi>{watcher.name}</bdi></div>
        <div className="ds">{live ? `The replay, ${watcher.stay.replace(" so far", "")}` : watcher.stay}</div>
      </div>
    </button>
  ) : null;

  if (live && live.state !== "ended" && !live.series?.ended) {
    const seats = live.series?.seats ?? live.registered;
    const faces = detail.recent_registrants.slice(0, 5).map((r) => ({ name: r.name }));
    return (
      <section className="card">
        <div className="card-b">
          <div className="eyebrow">{nextEyebrow(live, now)}</div>
          <div className="h2 mt4" style={{ fontSize: 18 }}>
            {longDay(live.startsAt, live.timezone)}
          </div>
          <div className="muted small">
            {labelParts(live.startsAt, live.timezone, "long")?.time}
            {live.series ? ` · session ${live.seq}` : ""}
          </div>
          <div className="aud mt12">
            {faces.length ? <FaceStack people={faces} size={28} more={Math.max(0, seats - faces.length)} /> : null}
            <div className="t">
              <b>{n(seats)} registered</b> so far
            </div>
          </div>
          <div className="row g8 mt16 wrap">
            <Button size="s" variant="p" icon="live" onClick={() => navigate(studioPath(detail.id))}>
              {live.state === "live" ? "Back to the studio" : "Open the studio"}
            </Button>
            <span className="rail-time">
              <Button size="s" onClick={onChangeTime}>
                Change the time
              </Button>
              {changeTime}
            </span>
          </div>
          {watchingRow ? (
            <>
              <div className="divider" />
              {watchingRow}
            </>
          ) : null}
        </div>
      </section>
    );
  }

  if (live) return <WasLive detail={detail} live={live} watchingRow={watchingRow} onWatchReplay={onWatchReplay} />;

  const shown = detail.watching.slice(0, 5);
  return (
    <section className="card">
      <div className="card-b">
        <div className="eyebrow">Watching right now</div>
        <div className="aud mt12">
          {shown.length ? <FaceStack people={shown.map((p) => ({ name: p.name, photo: p.avatar_url }))} size={30} /> : null}
          <div className="t">
            {shown.length ? (
              <>
                <b className="goodc">
                  {n(detail.watching.length)} {detail.watching.length === 1 ? "person" : "people"}
                </b>
                <br />
                {watchLine(detail)}
              </>
            ) : (
              "No one this minute"
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/** "Megan is 31 min in, Noah 9." */
export function watchLine(detail: WebinarDetail): string {
  const w = detail.watching;
  const mins = (p: (typeof w)[number]) => p.stay.split(" ")[0];
  if (w.length === 1) return `${firstOf(w[0].name)} is ${mins(w[0])} min in`;
  if (w.length === 2) return `${firstOf(w[0].name)} is ${mins(w[0])} min in, ${firstOf(w[1].name)} ${mins(w[1])}`;
  return namesLine(w, w.length);
}

export function RoomRail({
  detail,
  talk,
  onChangeTime,
  changeTime,
  onWatchReplay,
}: {
  detail: WebinarDetail;
  talk: HostRoomRow | null;
  onChangeTime: () => void;
  changeTime: ReactNode;
  onWatchReplay: () => void;
}) {
  const navigate = useNavigate();
  const sources = detail.reg_stats?.sources ?? [];
  const total = sources.reduce((sum, s) => sum + s.registered, 0);
  const rows = sources
    .filter((s) => s.registered > 0)
    .toSorted((a, b) => b.registered - a.registered)
    .slice(0, 5);
  const recent = detail.recent_registrants.slice(0, 3);
  const nowSec = Math.floor(Date.now() / 1000);
  const tz = detail.live?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <div className="col rail" style={{ gap: 20 }}>
      {talk ? <RoomTalkCard row={talk} /> : null}
      <TopCard detail={detail} onChangeTime={onChangeTime} changeTime={changeTime} onWatchReplay={onWatchReplay} />
      <RoomLanguage id={detail.id} language={detail.language} heard={detail.heard_language} />
      {rows.length ? (
        <section className="card">
          <div className="card-h">
            <h2>Where they came from</h2>
          </div>
          <div className="card-b" style={{ paddingTop: 10 }}>
            {rows.map((s, i) => {
              const pct = total ? Math.round((s.registered / total) * 100) : 0;
              return (
                <div key={`${s.source}:${s.detail ?? ""}`} className="srcrow">
                  <span className="ink2">{s.detail ? `${s.source}, ${s.detail}` : s.source}</span>
                  <div className={i ? "bar brassb" : "bar"}>
                    {/* Why against the first: the biggest source fills its bar, as Home's sources do. */}
                    <i style={{ width: `${rows[0]?.registered ? Math.round((s.registered / rows[0].registered) * 100) : 0}%` }} />
                  </div>
                  <b className="tr">{pct}%</b>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}
      {recent.length ? (
        <section className="card">
          <div className="card-h">
            <h2>Recent registrants</h2>
            <button type="button" className="link" onClick={() => navigate(`/people?webinar=${encodeURIComponent(detail.id)}`)}>
              All
            </button>
          </div>
          <div className="card-b col" style={{ gap: 10, paddingTop: 12 }}>
            {recent.map((r) => (
              <button key={r.id} type="button" className="who whobtn" onClick={() => navigate(`/people/${r.id}`)}>
                <Face name={r.name} size={36} />
                <div className="sp">
                  <div className="nm"><bdi>{r.name}</bdi></div>
                  {r.source ? <div className="ds">{r.source}</div> : null}
                </div>
                <span className="xs muted nowrap">{whenRegistered(r.created_at, nowSec, tz)}</span>
              </button>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
